/**
 * Customer data form configuration.
 *
 * Please see further documentation in the `src/types.ts` file.
 */
import { ConfigurableFormConfig, JSONSchema7Object } from "../../../types";
import { isPostCodeRequired } from "../../../utils/countries";
import { get, isNotEmpty } from "../../../utils/helpers";
import * as formElements from "../../shared/forms/formElements";
import formElementsSchema from "../../shared/forms/formElements.json";
import validateSchema from "./customerData.json";
import celineFormElementsSchema from "./formElements.json";

export const formConfig: ConfigurableFormConfig = {
  fields: [
    {
      ...formElements.group,
      id: "customer-name",
      props: {
        className: "customer-name"
      },
      showOnlyIf: {
        not: {
          has: {
            "pageState.language": "ja"
          }
        }
      },
      fields: [
        {
          ...formElements.fieldCustomPicklist,
          id: "honorificPrefixMy",
          name: "honorificPrefixMy",
          label: "customer.data.fields.honorificPrefix.label",
          props: {
            required: true
          },
          showOnlyIf: {
            has: {
              "appState.zone": "my"
            }
          },
          errorMessage: "customer.data.fields.honorificPrefix.errors.required"
        },
        {
          ...formElements.fieldTitle,
          id: "honorificPrefix",
          name: "honorificPrefix",
          label: "customer.data.fields.honorificPrefix.label",
          props: {
            required: true
          },
          showOnlyIf: {
            not: {
              has: {
                "appState.zone": "my"
              }
            }
          },
          errorMessage: "customer.data.fields.honorificPrefix.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        }
      ]
    },
    {
      ...formElements.group,
      id: "customer-name-ja",
      props: {
        className: "customer-name"
      },
      showOnlyIf: {
        has: {
          "pageState.language": "ja"
        }
      },
      fields: [
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateFamilyName2",
          name: "alternateFamilyName2",
          label: "customer.additionalInfo.fields.jpKanaLastName.label",
          showOnlyIf: {
            has: {
              "pageState.language": "ja"
            }
          },
          errorMessage:
            "customer.additionalInfo.fields.jpKanaLastName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateGivenName2",
          name: "alternateGivenName2",
          label: "customer.additionalInfo.fields.jpKanaFirstName.label",
          showOnlyIf: {
            has: {
              "pageState.language": "ja"
            }
          },
          errorMessage:
            "customer.additionalInfo.fields.jpKanaFirstName.errors.required"
        },
        {
          ...formElements.fieldTitle,
          id: "honorificPrefix",
          name: "honorificPrefix",
          label: "customer.data.fields.honorificPrefix.label",
          props: {
            required: true
          },
          errorMessage: "customer.data.fields.honorificPrefix.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateFamilyName1",
          name: "alternateFamilyName1",
          label: "customer.additionalInfo.fields.globalLastName.label",
          showOnlyIf: {
            has: {
              "pageState.language": "ja"
            }
          },
          errorMessage:
            "customer.additionalInfo.fields.globalLastName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateGivenName1",
          name: "alternateGivenName1",
          label: "customer.additionalInfo.fields.globalFirstName.label",
          showOnlyIf: {
            has: {
              "pageState.language": "ja"
            }
          },
          errorMessage:
            "customer.additionalInfo.fields.globalFirstName.errors.required"
        }
      ]
    },
    {
      ...formElements.fieldBirthDate,
      id: "birthDate",
      name: "birthDate",
      label: "customer.data.fields.birthDate.label",
      props: {
        inputProps: {
          labels: {
            day: "customer.data.fields.birthDate.options.day.label",
            month: "customer.data.fields.birthDate.options.month.label",
            year: "customer.data.fields.birthDate.options.year.label"
          },
          minimumAge: 16
        }
      },
      stateToProps: {
        "pageState.language": "inputProps.lang",
        "appState.storeCountry": "inputProps.country"
      },
      errorMessage: {
        _: "customer.data.fields.birthDate.errors.required",
        minimumAge:
          "customer.data.fields.birthDate.options.year.errors.underMinimumAge"
      }
    },
    {
      ...formElements.fieldCountry,
      id: "nationality",
      name: "nationality",
      label: "customer.data.fields.nationality.label",
      errorMessage: "customer.data.fields.nationality.errors.required",
      props: {
        required: false
      }
    },
    {
      ...formElements.fieldCountry,
      id: "addressCountry",
      name: "addressCountry",
      label: "customer.data.fields.address.country.label",
      errorMessage: "customer.data.fields.address.country.errors.required",
      props: {
        required: true
      }
    },
    {
      ...formElements.group,
      id: "customer-address",
      props: {
        className: "customer-address"
      },
      showOnlyIf: {
        all: {
          match: {
            "formState.addressCountry": isNotEmpty
          },
          not: {
            has: {
              "pageState.language": "ja"
            }
          }
        }
      },
      fields: [
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "streetAddress1",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          stateToProps: {
            "formState.postConsent": "required",
            "formState.addressCountry": "inputProps.country"
          },
          errorMessage: "customer.data.fields.address.line1.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "streetAddress2",
          name: "streetAddress2",
          label: "customer.data.fields.address.line2.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "addressLocality",
          name: "addressLocality",
          label: "customer.data.fields.address.city.label",
          props: {
            inputProps: {
              maxLength: 35
            }
          },
          stateToProps: {
            "formState.postConsent": "required"
          },
          errorMessage: "customer.data.fields.address.city.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "addressRegion",
          name: "addressRegion",
          label: "customer.data.fields.address.region.label",
          stateToProps: {
            "formState.addressCountry": "inputProps.country"
          },
          errorMessage: "customer.data.fields.address.region.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "postalCode",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            required: (value: any, propName?: string, input?: any) =>
              get(input, "formState.postConsent") &&
              get(input, "formState.addressCountry") &&
              isPostCodeRequired(get(input, "formState.addressCountry"))
          },
          errorMessage: "customer.data.fields.address.postCode.errors.required"
        }
      ]
    },
    {
      ...formElements.group,
      id: "customer-address-ja",
      props: {
        className: "customer-address"
      },
      showOnlyIf: {
        all: {
          match: {
            "formState.addressCountry": isNotEmpty
          },
          has: {
            "pageState.language": "ja"
          }
        }
      },
      fields: [
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "postalCode",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            required: (value: any, propName?: string, input?: any) =>
              get(input, "formState.postConsent") &&
              get(input, "formState.addressCountry") &&
              isPostCodeRequired(get(input, "formState.addressCountry"))
          },
          errorMessage: "customer.data.fields.address.postCode.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "addressRegion",
          name: "addressRegion",
          label: "customer.data.fields.address.region.label",
          stateToProps: {
            "formState.addressCountry": "inputProps.country"
          },
          errorMessage: "customer.data.fields.address.region.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "addressLocality",
          name: "addressLocality",
          label: "customer.data.fields.address.city.label",
          props: {
            inputProps: {
              maxLength: 35
            }
          },
          stateToProps: {
            "formState.postConsent": "required"
          },
          errorMessage: "customer.data.fields.address.city.errors.required"
        },
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "streetAddress1",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          stateToProps: {
            "formState.postConsent": "required",
            "formState.addressCountry": "inputProps.country"
          },
          errorMessage: "customer.data.fields.address.line1.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "streetAddress2",
          name: "streetAddress2",
          label: "customer.data.fields.address.line2.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          }
        }
      ]
    },
    {
      ...formElements.fieldEmail,
      id: "email",
      name: "email",
      label: "customer.data.fields.email.label",
      props: {
        inputProps: {
          maxLength: 250
        }
      },
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.emailConsent") ||
          get(input, "formState.isRemote")
      },
      errorMessage: {
        _: "customer.data.fields.email.errors.required",
        format: "customer.data.fields.email.errors.invalid"
      }
    },
    {
      ...formElements.fieldPhone,
      id: "telephone",
      name: "telephone",
      label: "customer.data.fields.phone.label",
      props: {
        inputProps: {
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label"
          }
        }
      },
      stateToProps: {
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country",
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.phoneConsent") &&
          (!get(input, "formState.mobileTelephone") ||
            get(input, "formState.telephone"))
      },
      errorMessage: {
        _: "customer.data.fields.phone.errors.required",
        pattern: "customer.data.fields.phone.errors.required"
      }
    },
    {
      ...formElements.fieldPhone,
      id: "mobileTelephone",
      name: "mobileTelephone",
      label: "customer.data.fields.mobilePhone.label",
      props: {
        inputProps: {
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label"
          }
        }
      },
      stateToProps: {
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country",
        required: (value: any, propName?: string, input?: any) =>
          (get(input, "formState.phoneConsent") &&
            (!get(input, "formState.telephone") ||
              get(input, "formState.mobileTelephone"))) ||
          get(input, "formState.appsConsent")
      },
      errorMessage: {
        _: "customer.data.fields.mobilePhone.errors.required",
        pattern: "customer.data.fields.mobilePhone.errors.required"
      }
    },
    {
      ...formElements.fieldText,
      id: "wechatId",
      name: "wechatId",
      label: "customer.data.fields.wechatId.label",
      props: {
        inputProps: {
          // @NOTE this is enforced in celineFormElements#/definitions/wechatId
          // minLength: 6,
          maxLength: 20
        }
      },
      errorMessage: {
        minLength: "customer.data.fields.wechatId.errors.invalid"
      }
    },
    {
      ...formElements.fieldText,
      id: "lineId",
      name: "lineId",
      label: "customer.data.fields.lineId.label",
      props: {
        inputProps: {
          maxLength: 30
        }
      }
    },
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.communicationMethods.title"
    },
    {
      ...formElements.sectionText,
      label: "customer.data.sections.communicationMethods.intro"
    },
    {
      ...formElements.fieldCheckbox,
      id: "emailConsent",
      name: "emailConsent",
      label: "customer.data.fields.emailConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "phoneConsent",
      name: "phoneConsent",
      label: "customer.data.fields.phoneConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "postConsent",
      name: "postConsent",
      label: "customer.data.fields.postConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "appsConsent",
      name: "appsConsent",
      label: "customer.data.fields.appsConsent.label"
    }
  ],
  validateSchema: validateSchema as JSONSchema7Object,
  refSchemas: [
    formElementsSchema as JSONSchema7Object,
    celineFormElementsSchema as JSONSchema7Object
  ],
  labels: {
    buttonBack: "general.buttons.cancel.label",
    buttonNext: "general.buttons.continue.label",
    buttonSubmit: "customer.data.buttons.next.label"
  }
};

export default formConfig;
